/**
 * XSSMonitor - Monitor de detección XSS
 * 
 * Observa el DOM mediante MutationObserver para detectar inserciones sospechosas
 * (<script>, atributos peligrosos). Intercepta innerHTML, insertAdjacentHTML
 * y document.write. Escanea parámetros de URL al inicializar. Reporta eventos
 * XSS al transport cuando detecta patrones sospechosos.
 */

import XSSDetector from './xss-detector.js';
import { ADR_CONSTANTS } from '../core/constants.js';

const XSS_DEDUPE_TTL_MS = 5000;
const XSS_DEDUPE_PAYLOAD_MAX_LEN = 256;

export class XSSMonitor {
	constructor() {
		this.detector = new XSSDetector();
		this.transport = null;
		this.config = null;
		this.observer = null;
		this.originalInnerHTML = null;
		this.originalInsertAdjacentHTML = null;
		this.originalDocumentWrite = null;
		this.hooksActive = false;
		this._dedupeMap = null;
		this._rateLimitTimestamps = null;
	}

	start(transport, config = null) {
		if (typeof window === 'undefined') {
			return;
		}

		this.transport = transport;
		this.config = config;
		this._dedupeMap = new Map();
		this._rateLimitTimestamps = [];

		this.startMutationObserver();
		this.scanURL();
		this.setupDOMHooks();
	}

	stop() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}

		this.restoreDOMHooks();
		this._dedupeMap = null;
		this._rateLimitTimestamps = null;
		this.config = null;
	}

	_getOption(key, defaultValue) {
		if (!this.config || typeof this.config.get !== 'function') return defaultValue;
		const v = this.config.get(key);
		return v !== undefined && v !== null ? v : defaultValue;
	}

	/**
	 * Rate limit numérico (xssMaxPerMinute), independiente del dedupe por
	 * variant/payload de arriba — un storm de payloads DISTINTOS genera una
	 * clave de dedupe distinta cada vez y lo evade por completo; esto lo cubre.
	 */
	_isRateLimited() {
		if (!this._rateLimitTimestamps) return false;
		const maxPerMin = this._getOption('xssMaxPerMinute', 10);
		const now = Date.now();
		const oneMinAgo = now - 60000;
		this._rateLimitTimestamps = this._rateLimitTimestamps.filter(ts => ts > oneMinAgo);
		if (this._rateLimitTimestamps.length >= maxPerMin) {
			return true;
		}
		this._rateLimitTimestamps.push(now);
		return false;
	}

	startMutationObserver() {
		if (typeof MutationObserver === 'undefined') {
			return;
		}

		const self = this;
		this.observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.type === 'childList') {
					for (const node of mutation.addedNodes) {
						if (node.nodeType === 1) {
							self.checkElement(node);
						}
					}
				} else if (mutation.type === 'attributes') {
					self.checkElement(mutation.target);
				}
			}
		});

		const observeTarget = () => {
			if (document.body) {
				this.observer.observe(document.body, {
					childList: true,
					subtree: true,
					attributes: true,
					attributeFilter: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur', 'oninput', 'onsubmit']
				});
			} else if (document.documentElement) {
				this.observer.observe(document.documentElement, {
					childList: true,
					subtree: true,
					attributes: true,
					attributeFilter: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur', 'oninput', 'onsubmit']
				});
			}
		};

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', observeTarget);
		} else {
			observeTarget();
		}
	}

	checkElement(element) {
		if (!element || element.nodeType !== 1) {
			return;
		}

		let contentToCheck = '';

		if (element.tagName === 'SCRIPT') {
			contentToCheck = element.outerHTML || element.innerHTML || '';
		} else {
			const dangerousAttrs = ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur', 'oninput', 'onsubmit'];
			for (const attr of dangerousAttrs) {
				if (element.hasAttribute(attr)) {
					contentToCheck = `${attr}=${element.getAttribute(attr) || ''}`;
					break;
				}
			}
			// Escanear atributo style para expression() y url("javascript:")
			if (!contentToCheck && element.hasAttribute('style')) {
				const styleValue = element.getAttribute('style') || '';
				if (styleValue.includes('expression(') || styleValue.includes('url("javascript:') || styleValue.includes("url('javascript:")) {
					contentToCheck = `style=${styleValue}`;
				}
			}
		}

		if (contentToCheck) {
			const detection = this.detector.detect(contentToCheck, { source: 'dom' });
			if (detection && detection.detected) {
				this.reportDetection(
					detection.sample,
					detection.severity,
					'dom',
					{
						variant: detection.variant,
						cvssScore: detection.cvssScore,
						cvssVector: detection.cvssVector,
						owaspCategory: detection.owaspCategory,
						cweId: detection.cweId,
						mitreTechnique: detection.mitreTechnique
					}
				);
			}
		}
	}

	scanURL() {
		if (typeof window === 'undefined' || !window.location) {
			return;
		}

		const url = window.location.href;
		const searchParams = new URLSearchParams(window.location.search);

		for (const [key, value] of searchParams.entries()) {
			if (value) {
				const detection = this.detector.detect(value, { source: 'url' });
				if (detection && detection.detected) {
					this.reportDetection(
						detection.sample,
						detection.severity,
						'url',
						{
							variant: detection.variant,
							cvssScore: detection.cvssScore,
							cvssVector: detection.cvssVector,
							owaspCategory: detection.owaspCategory,
							cweId: detection.cweId,
							mitreTechnique: detection.mitreTechnique
						}
					);
				}
			}
		}

		const detection = this.detector.detect(url, { source: 'url' });
		if (detection && detection.detected) {
			this.reportDetection(
				detection.sample,
				detection.severity,
				'url',
				{
					variant: detection.variant,
					cvssScore: detection.cvssScore,
					cvssVector: detection.cvssVector,
					owaspCategory: detection.owaspCategory,
					cweId: detection.cweId,
					mitreTechnique: detection.mitreTechnique
				}
			);
		}
	}

	scanInput(value) {
		if (!value || typeof value !== 'string') {
			return null;
		}

		const detection = this.detector.detect(value, { source: 'input' });
		if (detection && detection.detected) {
			this.reportDetection(
				detection.sample,
				detection.severity,
				'input',
				{
					variant: detection.variant,
					cvssScore: detection.cvssScore,
					cvssVector: detection.cvssVector,
					owaspCategory: detection.owaspCategory,
					cweId: detection.cweId,
					mitreTechnique: detection.mitreTechnique
				}
			);
			return detection;
		}

		return null;
	}

	setupDOMHooks() {
		if (this.hooksActive) {
			return;
		}

		try {
			if (Element.prototype && Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML')) {
				this.originalInnerHTML = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
				const self = this;

				Object.defineProperty(Element.prototype, 'innerHTML', {
					set: function(value) {
						try {
							if (value && typeof value === 'string') {
								const detection = self.detector.detect(value, { source: 'dom' });
								if (detection && detection.detected) {
									self.reportDetection(
										detection.sample,
										detection.severity,
										'dom',
										{
											variant: detection.variant,
											cvssScore: detection.cvssScore,
											cvssVector: detection.cvssVector,
											owaspCategory: detection.owaspCategory,
											cweId: detection.cweId,
											mitreTechnique: detection.mitreTechnique
										}
									);
								}
							}
						} catch (e) {
						}

						try {
							if (self.originalInnerHTML && self.originalInnerHTML.set) {
								self.originalInnerHTML.set.call(this, value);
							}
						} catch (e) {
						}
					},
					get: function() {
						if (self.originalInnerHTML && self.originalInnerHTML.get) {
							return self.originalInnerHTML.get.call(this);
						}
						return null;
					},
					configurable: true
				});
			}
		} catch (e) {
		}

		try {
			if (Element.prototype && Element.prototype.insertAdjacentHTML) {
				this.originalInsertAdjacentHTML = Element.prototype.insertAdjacentHTML;
				const self = this;

				Element.prototype.insertAdjacentHTML = function(position, html) {
					try {
						if (html && typeof html === 'string') {
							const detection = self.detector.detect(html, { source: 'dom' });
							if (detection && detection.detected) {
								self.reportDetection(
									detection.sample,
									detection.severity,
									'dom',
									{
										variant: detection.variant,
										cvssScore: detection.cvssScore,
										cvssVector: detection.cvssVector,
										owaspCategory: detection.owaspCategory,
										cweId: detection.cweId,
										mitreTechnique: detection.mitreTechnique
									}
								);
							}
						}
					} catch (e) {
					}

					try {
						if (self.originalInsertAdjacentHTML) {
							return self.originalInsertAdjacentHTML.call(this, position, html);
						}
					} catch (e) {
						throw e;
					}
				};
			}
		} catch (e) {
		}

		try {
			if (typeof document !== 'undefined' && document.write) {
				this.originalDocumentWrite = document.write;
				const self = this;

				document.write = function(content) {
					try {
						if (content && typeof content === 'string') {
							const detection = self.detector.detect(content, { source: 'dom' });
							if (detection && detection.detected) {
								self.reportDetection(
									detection.sample,
									detection.severity,
									'dom',
									{
										variant: detection.variant,
										cvssScore: detection.cvssScore,
										cvssVector: detection.cvssVector,
										owaspCategory: detection.owaspCategory,
										cweId: detection.cweId,
										mitreTechnique: detection.mitreTechnique
									}
								);
							}
						}
					} catch (e) {
					}

					try {
						if (self.originalDocumentWrite) {
							return self.originalDocumentWrite.call(document, content);
						}
					} catch (e) {
						throw e;
					}
				};
			}
		} catch (e) {
		}

		this.hooksActive = true;
	}

	restoreDOMHooks() {
		if (!this.hooksActive) {
			return;
		}

		try {
			if (this.originalInnerHTML && Element.prototype) {
				Object.defineProperty(Element.prototype, 'innerHTML', this.originalInnerHTML);
			}
		} catch (e) {
		}

		try {
			if (this.originalInsertAdjacentHTML && Element.prototype) {
				Element.prototype.insertAdjacentHTML = this.originalInsertAdjacentHTML;
			}
		} catch (e) {
		}

		try {
			if (this.originalDocumentWrite && typeof document !== 'undefined') {
				document.write = this.originalDocumentWrite;
			}
		} catch (e) {
		}

		this.hooksActive = false;
	}

	reportDetection(sample, severity, source, metadata = null) {
		if (!this.transport) {
			return;
		}

		if (this._dedupeMap) {
			const origin = typeof window !== 'undefined' && window.location ? window.location.href : '';
			const variant = (metadata && metadata.variant) ? String(metadata.variant) : '';
			const payloadFingerprint = (typeof sample === 'string' ? sample : String(sample)).substring(0, XSS_DEDUPE_PAYLOAD_MAX_LEN);
			const key = origin + '\n' + (variant || payloadFingerprint);
			const now = Date.now();
			for (const [k, expiresAt] of this._dedupeMap.entries()) {
				if (expiresAt <= now) this._dedupeMap.delete(k);
			}
			if (this._dedupeMap.has(key)) {
				return;
			}
			this._dedupeMap.set(key, now + XSS_DEDUPE_TTL_MS);
		}

		if (this._isRateLimited()) {
			return;
		}

		const meta = {
			source: source
		};

		if (metadata) {
			if (metadata.variant) meta.variant = metadata.variant;
			if (metadata.cvssScore !== undefined) meta.cvss_score = metadata.cvssScore;
			if (metadata.cvssVector) meta.cvss_vector = metadata.cvssVector;
			if (metadata.owaspCategory) meta.owasp_category = metadata.owaspCategory;
			if (metadata.cweId) meta.cwe_id = metadata.cweId;
			if (metadata.mitreTechnique) meta.mitre_technique = metadata.mitreTechnique;
		}

		this.transport.enqueue(
			ADR_CONSTANTS.EVENT_TYPES.XSS,
			sample,
			severity,
			meta
		);
	}
}

export default XSSMonitor;

